import DynamicImage from '@/components/commons/dynamic-image';
import {
  CollectionElementDataType,
  CollectionListElementDataType,
} from '@/components/sections/builder/elements/body/collection-list/type';
import CollectionSetting from '@/components/sections/builder/settings/collection-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { cn, isEmpty, truncateText } from '@/lib/utils';

import { ThemeType, useBuilderStore } from '@/stores/builder';
import { Tags } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { shallow } from 'zustand/shallow';

const defaultData: CollectionElementDataType = {
  name: 'Collection',
  collection: {
    collection_name: 'Collection 1',
    collection_id: '1',
    slug: 'collection-1',
    img_url: 'https://via.placeholder.com/300',
    description:
      'Lorem ipsum dolor sit amet consectetur adipiscing elit, magnis augue lectus eros dignissim ligula, laoreet elementum luctus eleifend quisque morbi.Collection description',
  },
};

const Content = ({
  parentData,
  data,
  theme,
}: {
  parentData: CollectionListElementDataType;
  data: CollectionElementDataType;
  theme: ThemeType;
}) => {
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const token = query.get('token') || '';
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  return (
    <>
      <Link
        href={`/collections/${data?.collection?.slug}${
          token ? `?theme_id=${themeId}&token=${token}` : ''
        }`}
      >
        <div
          className={cn(
            'relative h-full w-full overflow-hidden',
            !parentData.styleCollection ? 'theme_collection_card' : ''
          )}
        >
          <div
            className={cn(
              'collection-image-radius relative w-full overflow-hidden duration-300 hover:scale-[1.01]',
              parentData.imageRatio === 'adaptToImage' && 'img_ratio_adapt',
              parentData.imageRatio === 'portrait' && 'img_ratio_portrait',
              parentData.imageRatio === 'square' && 'img_ratio_square'
            )}
          >
            <DynamicImage
              key={parentData.imageRatio}
              className={cn(
                'collection-image-radius h-full w-full bg-transparent object-cover transition-all duration-300 ease-in-out'
              )}
              src={data?.collection?.img_url as string}
              alt={(data?.collection?.collection_name as string) || 'Collection image'}
              height={100}
              width={100}
              sizes='100vw'
            />
          </div>
          <div className={cn('py-4 pb-1')}>
            <Link href={`collections/${data?.collection?.slug}`}>
              <h1
                className={cn(
                  'collection_title mb-2  flex  gap-x-2 text-lg tracking-tight',
                  parentData.size === 'small' && 'text-sm',
                  parentData.size === 'medium' && 'text-base',
                  parentData.size === 'large' && 'text-lg',
                  theme.collection?.textAlignment === 'left' && 'justify-start',
                  theme.collection?.textAlignment === 'center' && 'justify-center',
                  theme.collection?.textAlignment === 'right' && 'justify-end',
                  !isEmpty(parentData.colorSchemeCollectionList) && 'button-scheme'
                )}
              >
                {truncateText(
                  data?.collection?.collection_name ?? '',
                  builder.theme.collection?.limitCharacters as number
                )}
              </h1>
            </Link>
            <div
              className={cn('collection_description minimal-tiptap-editor tiptap ProseMirror h-24')}
              data-animation='true'
              dangerouslySetInnerHTML={{ __html: data.collection?.description ?? '' }}
            ></div>
          </div>
          {parentData.showViewCollection && (
            <div
              className={cn(
                'flex h-full text-lg',
                parentData.viewCollectionPosition === 'left' && 'justify-start',
                parentData.viewCollectionPosition === 'right' && 'justify-end'
              )}
            >
              <Link href={`/collections/${data?.collection?.slug}`} className='hover:underline'>
                {parentData.labelViewCollection}
              </Link>
            </div>
          )}
        </div>
      </Link>
      <style jsx>
        {`
          .img_ratio_adapt {
            height: auto !important;
            width: 100% !important;
          }
          .img_ratio_portrait {
            aspect-ratio: 3 / 4;
          }
          .img_ratio_square {
            aspect-ratio: 1 / 1;
          }
          .collection_title {
            font-size: ${parentData.itemTitleSize || 0}px;
            color: ${parentData.colorSchemeCollectionList?.textSecondary} !important;
            &:hover {
              color: ${parentData.colorSchemeCollectionList?.text} !important;
            }
          }
          .collection_description {
            font-size: ${parentData.itemDescriptionSize || 0}px;
            color: ${parentData.colorSchemeCollectionList?.textSecondary} !important;
            &:hover {
              color: ${parentData.colorSchemeCollectionList?.text} !important;
            }
          }
          .button-scheme {
            // TODO: untuk sementara diganti untuk kompetisi
            color: ${parentData.colorSchemeCollectionList?.text} !important;
          }
          .collection-image-radius {
            border-radius: ${parentData.imageRadius || 0}px !important;
          }
        `}
      </style>
    </>
  );
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as CollectionElementDataType;
  const parentData = (element.parentData || {}) as CollectionListElementDataType;
  const theme = element.theme;

  return (
    <>
      <div
        className={cn(
          'card-rounded color-scheme-collection collection-style flex h-full w-full flex-col justify-between overflow-hidden'
        )}
        data-animation='true'
        data-animation-group={`collection-item-${element.parentId}`}
      >
        <div className='h-full w-full'>
          <Content parentData={parentData} data={data} theme={theme as ThemeType} />
        </div>
      </div>
      <style jsx>
        {`
          .collection-style {
            background-color: ${parentData.styleCollection === 'standard'
              ? 'transparent !important'
              : 'white !important'};
          }
          .color-scheme-collection {
            background-color: ${parentData.colorSchemeCollectionList?.background ?? 'transparent'};
            color: ${parentData.colorSchemeCollectionList?.text ?? 'black'};
          }
          .bg-box {
            background: url(${data?.collection?.img_url});
            background-position-x: center;
            background-position-y: center;
            padding-top: 40%;
          }
          .card-rounded {
            border-radius: ${parentData.rounded || 0}px;
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Tags;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as CollectionElementDataType;

  return (
    <div>
      <CollectionSetting
        value={data.collection}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            collection: val,
          })
        }
      />
    </div>
  );
}

const Collection = {
  type: 'collection',
  group: 'collection_list',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default Collection;
